import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { teamApi, setCurrentTeamId } from '../lib/api';
import { useActiveTeamStore } from '../store/activeTeamStore';

/**
 * QA #4 #4 root cause: this hook used queryKey ['teams'], which collided
 * with ~19 other pages that ALSO use queryKey ['teams'] but with a
 * different queryFn shape (they unwrap to res.data, this one stores the
 * full Axios response). Whichever query loaded first won the cache, and
 * the other consumers got back an unexpectedly-shaped value — silently
 * breaking the page.
 *
 * Fix: namespace this hook's cache key to ['teams', 'list-axios-response']
 * so it can never collide with a page-local query that stores the
 * unwrapped body. Don't change the unwrap path here since it's used by
 * 30+ callsites — namespacing is the surgical fix.
 *
 * Active team selection (2026-04-13):
 *   - A persisted activeTeamStore holds the user's current tenant choice.
 *   - useTeamId returns the active team if it's still valid (user is a
 *     member of it), otherwise falls back to the first owned team, then
 *     to the first member team. This ensures a consultant invited to
 *     multiple tenants never silently operates on the wrong one.
 *   - If the active team is no longer accessible (user left, team
 *     deleted), the store is auto-cleared so the fallback takes over.
 */

interface TeamSummary {
  id: string;
  name: string;
  ownerId: string;
  owner?: { id: string; email: string; firstName?: string; lastName?: string };
}

interface TeamsPayload {
  teams: TeamSummary[];
  ownedTeams: TeamSummary[];
  memberTeams: TeamSummary[];
}

export function useTeamId() {
  const activeTeamId = useActiveTeamStore((s) => s.activeTeamId);
  const setActiveTeamId = useActiveTeamStore((s) => s.setActiveTeamId);

  const { data, isLoading } = useQuery({
    queryKey: ['teams', 'list-axios-response'],
    queryFn: () => teamApi.getTeams(),
    staleTime: 5 * 60 * 1000,
  });

  const payload = data?.data as TeamsPayload | undefined;

  // Resolve which team ID to use:
  //   1. activeTeamId if the user is still a member of it
  //   2. first owned team
  //   3. first member team
  //   4. first team in the combined list (legacy fallback)
  const resolvedTeamId = useMemo(() => {
    if (!payload) return undefined;
    const allIds = new Set(payload.teams?.map((t) => t.id) ?? []);
    if (activeTeamId && allIds.has(activeTeamId)) return activeTeamId;
    return (
      payload.ownedTeams?.[0]?.id ??
      payload.memberTeams?.[0]?.id ??
      payload.teams?.[0]?.id
    );
  }, [payload, activeTeamId]);

  // Auto-heal: if the persisted activeTeamId points at a team the user
  // can no longer access, clear it so future reads use the fallback.
  useEffect(() => {
    if (!payload || !activeTeamId) return;
    const allIds = new Set(payload.teams?.map((t) => t.id) ?? []);
    if (!allIds.has(activeTeamId)) {
      setActiveTeamId(null);
    }
  }, [payload, activeTeamId, setActiveTeamId]);

  // Push the resolved teamId to the api module so the request
  // interceptor can auto-attach it to teamId-implicit endpoints
  // (/fair-lending/*, /hmda/*, /lar-builder/*, etc.). Without this,
  // every multi-team user gets `400 team_id_required` from the
  // resolveTeam middleware on those routes.
  useEffect(() => {
    setCurrentTeamId(resolvedTeamId ?? null);
  }, [resolvedTeamId]);

  return {
    teamId: resolvedTeamId as string | undefined,
    isLoading,
  };
}

/**
 * Lightweight hook returning the user's full team list alongside the
 * active team ID setter. Used by TeamSwitcher to render the dropdown
 * without each consumer re-fetching /teams.
 */
export function useTeamSwitcher() {
  const activeTeamId = useActiveTeamStore((s) => s.activeTeamId);
  const setActiveTeamId = useActiveTeamStore((s) => s.setActiveTeamId);
  const { teamId } = useTeamId();

  const { data, isLoading } = useQuery({
    queryKey: ['teams', 'list-axios-response'],
    queryFn: () => teamApi.getTeams(),
    staleTime: 5 * 60 * 1000,
  });

  const payload = data?.data as TeamsPayload | undefined;
  const teams = payload?.teams ?? [];
  const ownedIds = new Set(payload?.ownedTeams?.map((t) => t.id) ?? []);

  return {
    teams,
    activeTeamId: (activeTeamId && teams.some((t) => t.id === activeTeamId) ? activeTeamId : teamId) ?? null,
    setActiveTeamId,
    ownedIds,
    isLoading,
  };
}
